/**
 * Keyword search and grouping for the Spec metafields picker (MOT-361). A port of the server's
 * `src/utils/specCatalogSearch.ts`: a spec is found by any word of the label a brand used, the canonical
 * key it maps to, its category and its known aliases, so "memory" surfaces Memory Card Type / Card Slots /
 * Storage Media whatever the sheet called it. `specCatalogSearch.test.ts` pins it to the server's fixtures.
 */

export interface SearchableSpec {
  label: string;
  productCount: number;
  specPath?: string;
  category?: string;
  aliases?: string[];
}

/** `mixed`: every word was found, but spread across the fields ("storage card" -> category + label). */
export type SpecSearchMatchedOn = 'label' | 'key' | 'category' | 'alias' | 'mixed';

export interface SpecSearchHit<T extends SearchableSpec> {
  entry: T;
  matchedOn: SpecSearchMatchedOn;
  /** The alias that answered the query, when the label itself did not. */
  alias?: string;
}

/** `storage.slotTypes` -> "storage slot types". */
export function specKeyWords(specPath: string | undefined): string {
  if (!specPath) return '';
  return specPath
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[._-]+/g, ' ')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** What a key is searched as: its words, the raw path and the bare leaf (`storage.slotTypes`, `slottypes`). */
export function specKeyText(specPath: string | undefined): string {
  if (!specPath) return '';
  const lower = specPath.toLowerCase();
  const leaf = lower.split('.').pop() || lower;
  return `${specKeyWords(specPath)} ${lower} ${leaf}`.trim();
}

export function normalizeSpecSearchText(value: string): string {
  return String(value || '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function specSearchWords(query: string): string[] {
  return normalizeSpecSearchText(query).split(' ').filter(Boolean);
}

const includesEveryWord = (text: string, words: string[]): boolean => words.every((word) => text.includes(word));

/** Where a query finds a spec, in precedence order (label beats category beats key beats alias); null when nowhere. */
export function matchSpecSearch<T extends SearchableSpec>(entry: T, query: string): SpecSearchHit<T> | null {
  const words = specSearchWords(query);
  if (words.length === 0) return { entry, matchedOn: 'label' };
  if (includesEveryWord(normalizeSpecSearchText(entry.label), words)) return { entry, matchedOn: 'label' };
  if (entry.category && includesEveryWord(normalizeSpecSearchText(entry.category), words)) {
    return { entry, matchedOn: 'category' };
  }
  if (includesEveryWord(specKeyText(entry.specPath), words)) return { entry, matchedOn: 'key' };
  for (const alias of entry.aliases || []) {
    if (includesEveryWord(normalizeSpecSearchText(alias), words)) return { entry, matchedOn: 'alias', alias };
  }
  const everything = [
    normalizeSpecSearchText(entry.label),
    specKeyText(entry.specPath),
    normalizeSpecSearchText(entry.category || ''),
    ...(entry.aliases || []).map(normalizeSpecSearchText),
  ].join(' | ');
  if (includesEveryWord(everything, words)) return { entry, matchedOn: 'mixed' };
  return null;
}

const MATCH_RANK: Record<SpecSearchMatchedOn, number> = { label: 0, category: 1, key: 2, alias: 3, mixed: 4 };

/**
 * The specs a query finds, label hits first, then category, key and alias hits, each group by how many
 * synced products carry the spec and then by label. An empty query returns everything by product count.
 */
export function searchSpecCatalog<T extends SearchableSpec>(entries: T[], query: string): Array<SpecSearchHit<T>> {
  const hits: Array<SpecSearchHit<T>> = [];
  for (const entry of entries) {
    const hit = matchSpecSearch(entry, query);
    if (hit) hits.push(hit);
  }
  return hits.sort(
    (a, b) =>
      MATCH_RANK[a.matchedOn] - MATCH_RANK[b.matchedOn] ||
      b.entry.productCount - a.entry.productCount ||
      a.entry.label.localeCompare(b.entry.label),
  );
}

export interface SpecCatalogGroup<T> {
  category: string;
  entries: T[];
  /** Sum of the entries' product counts (one product can carry several specs of a group). */
  productCount: number;
}

export const UNGROUPED_SPEC_CATEGORY = 'General';

/** Items grouped by their entry's category, in the order given (so a ranked list stays ranked inside each group). */
export function groupSpecCatalog<T extends { entry: SearchableSpec }>(items: T[]): Array<SpecCatalogGroup<T>> {
  const groups = new Map<string, SpecCatalogGroup<T>>();
  for (const item of items) {
    const category = item.entry.category?.trim() || UNGROUPED_SPEC_CATEGORY;
    let group = groups.get(category);
    if (!group) {
      group = { category, entries: [], productCount: 0 };
      groups.set(category, group);
    }
    group.entries.push(item);
    group.productCount += item.entry.productCount;
  }
  return Array.from(groups.values());
}
